import { useId, type ReactNode } from "react";

/**
 * One journey-map island (SPEC §9.5). Cartoon style: chunky rounded shapes, thick same-hue
 * outlines, round joins, soft highlights — no sharp corners.
 */
export function Island({
  biome,
  locked = false,
  size = 220,
}: {
  biome: string;
  locked?: boolean;
  size?: number;
}) {
  const uid = useId().replace(/:/g, "");
  const g = (n: string) => `${n}-${uid}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 240 170"
      width={size}
      height={(size * 170) / 240}
      aria-hidden="true"
      strokeLinejoin="round"
      strokeLinecap="round"
      style={locked ? { filter: "grayscale(0.85) opacity(0.75)" } : undefined}
    >
      <defs>
        <linearGradient id={g("grass")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a6e872" />
          <stop offset="1" stopColor="#62bd45" />
        </linearGradient>
        <linearGradient id={g("sand")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fdebbd" />
          <stop offset="1" stopColor="#eccb84" />
        </linearGradient>
      </defs>
      <ellipse cx="120" cy="140" rx="112" ry="24" fill="#c9eefc" opacity="0.9" />
      <ellipse cx="120" cy="136" rx="98" ry="19" fill="#ecf9ff" opacity="0.8" />
      <path
        d="M30 122 C30 104 70 98 120 98 C170 98 210 104 210 122 C210 142 170 150 120 150 C70 150 30 142 30 122 Z"
        fill={`url(#${g("sand")})`}
        stroke="#dcb56a"
        strokeWidth="3"
      />
      <path
        d="M36 114 C34 92 64 80 96 80 C112 72 140 72 156 80 C190 80 208 94 204 114 C198 128 168 132 120 132 C72 132 40 128 36 114 Z"
        fill={`url(#${g("grass")})`}
        stroke="#4fa83c"
        strokeWidth="3.5"
      />
      <ellipse cx="94" cy="92" rx="32" ry="8" fill="#c8f59d" opacity="0.65" />
      <circle cx="54" cy="128" r="5.5" fill="#c3cad6" stroke="#9aa5b8" strokeWidth="2" />
      <circle cx="190" cy="126" r="4.5" fill="#c3cad6" stroke="#9aa5b8" strokeWidth="2" />
      {DECOR[biome] ?? DECOR.meadow}
    </svg>
  );
}

const ink = "#6b4a2e";

const tree = (x: number, y: number, s = 1, fruit?: string): ReactNode => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <rect
      x="-5"
      y="-2"
      width="10"
      height="24"
      rx="5"
      fill="#b47a44"
      stroke="#8a5a2e"
      strokeWidth="2.5"
    />
    <circle cx="0" cy="-10" r="20" fill="#5cb846" stroke="#3f9632" strokeWidth="3" />
    <circle cx="-7" cy="-17" r="8" fill="#8fdc63" />
    {fruit &&
      [
        [-8, -6],
        [7, -13],
        [9, 0],
        [-1, -22],
      ].map(([fx, fy]) => (
        <circle
          key={`${fx}${fy}`}
          cx={fx}
          cy={fy}
          r="4"
          fill={fruit}
          stroke="#e07020"
          strokeWidth="1.5"
        />
      ))}
  </g>
);

const flower = (x: number, y: number, c: string) => (
  <g transform={`translate(${x} ${y})`}>
    {[0, 72, 144, 216, 288].map((a) => (
      <circle
        key={a}
        cx={Math.cos((a * Math.PI) / 180) * 4.5}
        cy={Math.sin((a * Math.PI) / 180) * 4.5}
        r="3.8"
        fill={c}
      />
    ))}
    <circle r="2.8" fill="#ffd54a" />
  </g>
);

/** soft round-topped cone (roofs, towers, tents) */
const cone = (x: number, w: number, top: number, base: number) =>
  `M${x - w / 2} ${base} C${x - w / 2} ${base - 6} ${x - 6} ${top + 4} ${x} ${top} C${x + 6} ${top + 4} ${x + w / 2} ${base - 6} ${x + w / 2} ${base} Z`;

const DECOR: Record<string, ReactNode> = {
  meadow: (
    <g>
      {tree(178, 90, 0.85)}
      <g transform="translate(110 50)">
        <path d="M2 0 v10" stroke={ink} strokeWidth="3.5" />
        <ellipse cx="2" cy="17" rx="15" ry="8" fill="#ffc727" stroke="#e39b00" strokeWidth="3" />
        <ellipse cx="2" cy="28" rx="20" ry="9" fill="#ffd54a" stroke="#e39b00" strokeWidth="3" />
        <ellipse cx="2" cy="40" rx="22" ry="9" fill="#ffc727" stroke="#e39b00" strokeWidth="3" />
        <ellipse cx="2" cy="41" rx="5" ry="5" fill={ink} />
        <ellipse cx="-6" cy="25" rx="6" ry="2.5" fill="#fff3b0" />
      </g>
      {flower(64, 106, "#ff8fb6")}
      {flower(84, 118, "#ffffff")}
      {flower(152, 114, "#a98bff")}
    </g>
  ),
  pond: (
    <g>
      <ellipse cx="116" cy="104" rx="44" ry="15" fill="#4cc0ee" stroke="#1e8fd0" strokeWidth="3" />
      <ellipse cx="102" cy="100" rx="15" ry="3.5" fill="#c9eefc" />
      <g transform="translate(124 88)">
        <ellipse cx="0" cy="8" rx="12" ry="8" fill="#ffd54a" stroke="#e3a400" strokeWidth="2.5" />
        <circle cx="9" cy="-1" r="7" fill="#ffd54a" stroke="#e3a400" strokeWidth="2.5" />
        <ellipse cx="17" cy="0" rx="5" ry="2.8" fill="#ff9a4a" />
        <circle cx="10" cy="-2" r="1.6" fill={ink} />
      </g>
      {tree(58, 90, 0.75)}
      {tree(184, 92, 0.85)}
    </g>
  ),
  market: (
    <g>
      <g transform="translate(84 50)">
        <rect
          x="2"
          y="22"
          width="68"
          height="36"
          rx="10"
          fill="#fff1cc"
          stroke="#d29a55"
          strokeWidth="3"
        />
        <path d="M-2 24 C-2 6 74 6 74 24" fill="#ff8a66" stroke="#e5603f" strokeWidth="3" />
        {[0, 1, 2, 3].map((i) => (
          <path
            key={i}
            d={`M${-2 + i * 19} 24 a9.5 8 0 0 0 19 0`}
            fill={i % 2 ? "#ffffff" : "#ff8a66"}
            stroke="#e5603f"
            strokeWidth="2.5"
          />
        ))}
        <circle cx="18" cy="46" r="7" fill="#ff9a4a" stroke="#e07020" strokeWidth="2" />
        <circle cx="35" cy="47" r="7" fill="#ffd54a" stroke="#e3a400" strokeWidth="2" />
        <circle cx="52" cy="46" r="7" fill="#8fdc63" stroke="#4fa83c" strokeWidth="2" />
      </g>
      {tree(186, 92, 0.75, "#ff9a4a")}
    </g>
  ),
  bamboo: (
    <g>
      {[78, 98, 140, 160, 178].map((x, i) => (
        <g key={x}>
          <rect
            x={x}
            y={34 + (i % 2) * 10}
            width="10"
            height={72 - (i % 2) * 10}
            rx="5"
            fill="#8fdc63"
            stroke="#4fa83c"
            strokeWidth="2.5"
          />
          {[54, 72, 90].map((y) => (
            <path key={y} d={`M${x + 1} ${y} h8`} stroke="#4fa83c" strokeWidth="2.5" />
          ))}
          <path
            d={`M${x + 9} ${46 + (i % 2) * 10} c8 -8 18 -6 20 0 c-8 4 -14 4 -20 0z`}
            fill="#a6e872"
            stroke="#4fa83c"
            strokeWidth="2"
          />
        </g>
      ))}
    </g>
  ),
  blocks: (
    <g>
      {[
        [80, 76, "#ff8a66", "#e5603f"],
        [114, 76, "#4a95f0", "#1e6fd9"],
        [97, 46, "#ffd54a", "#e3a400"],
        [150, 86, "#a98bff", "#7c5ce6"],
      ].map(([x, y, c, s]) => (
        <g key={`${x}${y}`}>
          <rect
            x={x as number}
            y={y as number}
            width="30"
            height="30"
            rx="9"
            fill={c as string}
            stroke={s as string}
            strokeWidth="3"
          />
          <rect
            x={(x as number) + 5}
            y={(y as number) + 5}
            width="12"
            height="6"
            rx="3"
            fill="#ffffff"
            opacity="0.55"
          />
        </g>
      ))}
      <path d={cone(64, 24, 92, 112)} fill="#5cc96f" stroke="#2a9a44" strokeWidth="3" />
      <circle cx="188" cy="104" r="11" fill="#ff8fb6" stroke="#d94f84" strokeWidth="3" />
    </g>
  ),
  clocktower: (
    <g>
      <rect
        x="104"
        y="44"
        width="36"
        height="62"
        rx="10"
        fill="#fff1cc"
        stroke="#d29a55"
        strokeWidth="3"
      />
      <path d={cone(122, 50, 14, 48)} fill="#ff8a66" stroke="#e5603f" strokeWidth="3" />
      <circle cx="122" cy="15" r="4" fill="#ffd54a" stroke="#e3a400" strokeWidth="2" />
      <circle cx="122" cy="66" r="13" fill="#ffffff" stroke={ink} strokeWidth="3" />
      <path d="M122 66 v-8 M122 66 l6 3" stroke={ink} strokeWidth="3" />
      <rect
        x="114"
        y="86"
        width="16"
        height="20"
        rx="8"
        fill="#b47a44"
        stroke="#8a5a2e"
        strokeWidth="2.5"
      />
      {tree(70, 92, 0.7)}
      {tree(180, 94, 0.8)}
    </g>
  ),
  castle: (
    <g>
      <rect
        x="84"
        y="58"
        width="72"
        height="48"
        rx="10"
        fill="#eef3fb"
        stroke="#9aa5b8"
        strokeWidth="3"
      />
      {[80, 148].map((x) => (
        <g key={x}>
          <rect
            x={x}
            y="44"
            width="22"
            height="62"
            rx="9"
            fill="#dcebff"
            stroke="#9aa5b8"
            strokeWidth="3"
          />
          <path d={cone(x + 11, 30, 18, 48)} fill="#a98bff" stroke="#7c5ce6" strokeWidth="3" />
          <path d={`M${x + 11} 18 v-8`} stroke="#9aa5b8" strokeWidth="2.5" />
          <path
            d={`M${x + 11} 10 c6 -2 8 2 12 0 c-2 4 -8 6 -12 4z`}
            fill="#ff8a66"
            stroke="#e5603f"
            strokeWidth="1.5"
          />
        </g>
      ))}
      <path
        d="M108 106 v-16 a12 12 0 0 1 24 0 v16z"
        fill="#b47a44"
        stroke="#8a5a2e"
        strokeWidth="3"
      />
      {[96, 120, 144].map((x) => (
        <circle key={x} cx={x} cy="58" r="6" fill="#eef3fb" stroke="#9aa5b8" strokeWidth="2.5" />
      ))}
    </g>
  ),
  orchard: (
    <g>
      {tree(80, 84, 0.95, "#ff9a4a")}
      {tree(126, 74, 1.05, "#ff9a4a")}
      {tree(172, 88, 0.9, "#ff9a4a")}
    </g>
  ),
  toys: (
    <g>
      <g transform="translate(82 62)">
        <path d="M0 6 v32 a15 6 0 0 0 30 0 v-32" fill="#4cc0ee" stroke="#1e8fd0" strokeWidth="3" />
        <ellipse cx="15" cy="6" rx="15" ry="6" fill="#a6e0fb" stroke="#1e8fd0" strokeWidth="3" />
      </g>
      <circle cx="142" cy="88" r="19" fill="#ff8a66" stroke="#e5603f" strokeWidth="3" />
      <path d="M125 84 q17 10 34 0" stroke="#ffffff" strokeWidth="3.5" fill="none" />
      <circle cx="135" cy="79" r="5" fill="#ffffff" opacity="0.6" />
      <circle cx="184" cy="104" r="10" fill="#ffd54a" stroke="#e3a400" strokeWidth="3" />
    </g>
  ),
  mountain: (
    <g>
      <path
        d="M60 110 C70 80 88 46 104 46 C120 46 138 80 148 110 Z"
        fill="#a3aec2"
        stroke="#6b7894"
        strokeWidth="3"
      />
      <path
        d="M90 66 C96 52 112 52 118 66 C112 70 108 64 104 70 C100 64 96 70 90 66 Z"
        fill="#ffffff"
      />
      <path
        d="M118 110 C126 88 138 62 150 62 C162 62 174 88 182 110 Z"
        fill="#c3cad6"
        stroke="#6b7894"
        strokeWidth="3"
      />
      <path
        d="M140 76 C144 68 156 68 160 76 C156 79 153 75 150 79 C147 75 144 79 140 76 Z"
        fill="#ffffff"
      />
      <rect
        x="36"
        y="86"
        width="30"
        height="18"
        rx="7"
        fill="#ffffff"
        stroke={ink}
        strokeWidth="2.5"
      />
      <text
        x="51"
        y="99"
        textAnchor="middle"
        fontSize="10.5"
        fontWeight="800"
        fill="#1e6fd9"
        fontFamily="sans-serif"
      >
        1000
      </text>
    </g>
  ),
  road: (
    <g>
      <path
        d="M50 120 Q90 92 120 104 Q160 118 196 86"
        stroke="#a3aec2"
        strokeWidth="16"
        fill="none"
      />
      <path
        d="M50 120 Q90 92 120 104 Q160 118 196 86"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeDasharray="5 8"
        fill="none"
      />
      <rect x="118" y="46" width="6" height="40" rx="3" fill="#b47a44" />
      <rect
        x="98"
        y="46"
        width="48"
        height="18"
        rx="9"
        fill="#5cc96f"
        stroke="#2a9a44"
        strokeWidth="3"
      />
      <text
        x="122"
        y="59"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="800"
        fill="#fff"
        fontFamily="sans-serif"
      >
        1 km
      </text>
      {tree(70, 86, 0.65)}
    </g>
  ),
  treasure: (
    <g>
      <g transform="translate(96 62)">
        <rect
          x="0"
          y="18"
          width="50"
          height="30"
          rx="8"
          fill="#d29a55"
          stroke={ink}
          strokeWidth="3"
        />
        <path d="M0 22 C0 2 50 2 50 22 Z" fill="#b47a44" stroke={ink} strokeWidth="3" />
        <rect
          x="20"
          y="16"
          width="10"
          height="13"
          rx="4"
          fill="#ffd54a"
          stroke={ink}
          strokeWidth="2.5"
        />
        <circle cx="10" cy="8" r="5.5" fill="#ffd54a" stroke="#e3a400" strokeWidth="2" />
        <circle cx="39" cy="6" r="5.5" fill="#ffd54a" stroke="#e3a400" strokeWidth="2" />
      </g>
      {tree(64, 90, 0.75)}
      <ellipse cx="184" cy="104" rx="9" ry="6" fill="#ff8fb6" stroke="#d94f84" strokeWidth="2.5" />
    </g>
  ),
  fair: (
    <g>
      <path d={cone(120, 74, 44, 108)} fill="#ffffff" stroke="#e5603f" strokeWidth="3" />
      <path d="M120 46 C114 70 112 92 112 108 L128 108 C128 92 126 70 120 46 Z" fill="#ff8a66" />
      <path d="M120 46 v-12" stroke="#e3a400" strokeWidth="2.5" />
      <path
        d="M120 34 c6 -2 9 3 14 0 c-3 5 -9 7 -14 5z"
        fill="#ffd54a"
        stroke="#e3a400"
        strokeWidth="1.5"
      />
      <path d="M48 72 Q78 86 106 68" stroke={ink} strokeWidth="2" fill="none" />
      {[58, 70, 82, 94].map((x, i) => (
        <circle
          key={x}
          cx={x}
          cy={79 + (i % 2) * 2}
          r="4.5"
          fill={["#a98bff", "#5cc96f", "#ffd54a", "#4a95f0"][i]}
        />
      ))}
      {[174, 185, 196].map((x, i) => (
        <rect
          key={x}
          x={x}
          y={96 - (i + 1) * 9}
          width="8"
          height={(i + 1) * 9 + 4}
          rx="4"
          fill="#4a95f0"
          stroke="#1e6fd9"
          strokeWidth="2"
        />
      ))}
    </g>
  ),
};
